<template>
  <div class="privacy container">
    <h1>Politica de Confidențialitate</h1>
    <p class="meta">
      Data intrării în vigoare: 8 noiembrie 2025 · Ultima actualizare: 8 noiembrie 2025
    </p>

    <hr />

    <section>
      <p>
        GAB16 („noi”, „al nostru” sau „compania”) operează site-ul <strong>gab16.com</strong> și
        jocul <strong>Șeptică Online</strong> (denumite împreună „Serviciul”). Această Politică de
        Confidențialitate explică modul în care colectăm, utilizăm și protejăm datele tale
        personale atunci când utilizezi Serviciul.
      </p>
    </section>

    <nav class="toc" aria-label="Cuprins">
      <h2>Ce conține această politică</h2>
      <ul>
        <li><a href="#information-we-collect">1. Datele pe care le colectăm</a></li>
        <li><a href="#how-we-use">2. Cum folosim informațiile tale</a></li>
        <li><a href="#legal-bases">3. Baza legală (UE/SEE)</a></li>
        <li><a href="#retention">4. Păstrarea datelor</a></li>
        <li><a href="#security">5. Securitatea datelor</a></li>
        <li><a href="#your-rights">6. Drepturile tale</a></li>
        <li><a href="#children">7. Confidențialitatea copiilor</a></li>
        <li><a href="#transfers">8. Transferuri internaționale de date</a></li>
        <li><a href="#third-parties">9. Servicii terțe</a></li>
        <li><a href="#contact">10. Contact</a></li>
        <li><a href="#changes">11. Modificări ale acestei politici</a></li>
      </ul>
    </nav>

    <section id="information-we-collect">
      <h2>1. Datele pe care le colectăm</h2>
      <p>
        Atunci când îți creezi un cont sau folosești Serviciul, putem colecta următoarele informații:
      </p>
      <table class="data-table" role="table">
        <thead>
          <tr>
            <th scope="col">Tip</th>
            <th scope="col">Descriere</th>
            <th scope="col">Obligatoriu</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Nume de utilizator</td>
            <td>
              Numele ales de tine, afișat în joc și în clasamente.
            </td>
            <td>Da</td>
          </tr>
          <tr>
            <td>Adresă de e-mail</td>
            <td>Folosită pentru autentificare, verificare și recuperarea contului.</td>
            <td>Da</td>
          </tr>
          <tr>
            <td>Parolă</td>
            <td>
              Stocată în siguranță prin algoritmi de criptare unidirecționali (niciodată în text clar).
            </td>
            <td>Da</td>
          </tr>
          <tr>
            <td>Imagine de profil</td>
            <td>
              Dacă alegi să o încarci, aceasta este afișată altor jucători împreună cu profilul tău.
            </td>
            <td>Opțional</td>
          </tr>
        </tbody>
      </table>
      <p>
        Nu colectăm <strong>categorii sensibile</strong> de date (de exemplu: CNP, informații de
        plată, date medicale) și <strong>nu vindem</strong> datele tale nimănui.
      </p>
    </section>

    <section id="how-we-use">
      <h2>2. Cum folosim informațiile tale</h2>
      <ul>
        <li>Creăm și administrăm contul tău de utilizator.</li>
        <li>
          Afișăm numele tău de utilizator și imaginea de profil (dacă există) în clasamente, liste
          de prieteni sau realizări.
        </li>
        <li>
          Oferim funcționalități de joc multiplayer și detectăm/împiedicăm abuzurile sau fraudele.
        </li>
        <li>Trimitem e-mailuri esențiale (ex: verificare, resetare parolă).</li>
        <li>Operăm, întreținem și îmbunătățim Serviciul.</li>
      </ul>
      <p>
        Nu partajăm datele tale personale cu terți în scopuri publicitare sau de marketing.
      </p>
    </section>

    <section id="legal-bases">
      <h2>3. Baza legală pentru prelucrare (UE/SEE)</h2>
      <ul>
        <li>
          <strong>Necesitatea contractuală</strong> – pentru a-ți oferi acces la Serviciu.
        </li>
        <li>
          <strong>Interes legitim</strong> – pentru a menține Serviciul sigur și funcțional.
        </li>
        <li>
          <strong>Consimțământ</strong> – pentru funcționalități opționale, precum încărcarea unei imagini de profil.
        </li>
      </ul>
    </section>

    <section id="retention">
      <h2>4. Păstrarea datelor</h2>
      <p>
        Păstrăm datele tale doar atât timp cât este necesar pentru scopurile menționate mai sus.
        Dacă îți ștergi contul sau soliciți ștergerea acestuia, datele tale vor fi eliminate
        permanent din sistemele noastre în termen de <strong>30 de zile</strong>, cu excepția cazurilor
        în care legea impune o perioadă mai lungă.
      </p>
    </section>

    <section id="security">
      <h2>5. Securitatea datelor</h2>
      <ul>
        <li>Parolele sunt criptate folosind algoritmi standard din industrie.</li>
        <li>Conexiunile către site și API sunt protejate prin HTTPS.</li>
        <li>
          Accesul la date este controlat și monitorizat pentru a preveni accesul neautorizat.
        </li>
      </ul>
      <p>
        Nicio măsură de securitate nu este perfectă, dar facem tot posibilul pentru a proteja
        informațiile tale.
      </p>
    </section>

    <section id="your-rights">
      <h2>6. Drepturile tale</h2>
      <p>
        <strong>UE/SEE (GDPR):</strong> Ai dreptul de a solicita accesul la datele tale, rectificarea
        lor, ștergerea, restricționarea prelucrării, portabilitatea datelor și de a te opune
        prelucrării.
      </p>
      <p>
        <strong>SUA / alte regiuni:</strong> În funcție de jurisdicția ta, este posibil să ai
        drepturi similare prevăzute de legislația locală.
      </p>
      <p>
        Pentru exercitarea acestor drepturi sau pentru a solicita ștergerea contului, trimite un
        e-mail la <a href="mailto:contact@gab16.com">contact@gab16.com</a>. Este posibil să fie
        necesar să îți verificăm identitatea înainte de a procesa cererea.
      </p>
    </section>

    <section id="children">
      <h2>7. Confidențialitatea copiilor</h2>
      <p>
        Serviciul nu este destinat copiilor sub <strong>16 ani</strong>. Nu colectăm intenționat
        informații personale de la minori. Dacă crezi că un copil ne-a furnizat date personale,
        te rugăm să ne contactezi pentru a le șterge.
      </p>
    </section>

    <section id="transfers">
      <h2>8. Transferuri internaționale de date</h2>
      <p>
        Datele tale pot fi procesate în Uniunea Europeană, unde sunt găzduite serverele noastre.
        Atunci când transferăm date, aplicăm măsuri adecvate de protecție în conformitate cu legea.
      </p>
    </section>

    <section id="third-parties">
      <h2>9. Servicii terțe</h2>
      <p>
        În prezent, nu împărtășim datele tale personale cu servicii terțe de analiză sau
        publicitate. Dacă acest lucru se va schimba, vom actualiza politica și vom solicita
        consimțământul tău acolo unde este necesar.
      </p>
    </section>

    <section id="contact">
      <h2>10. Contact</h2>
      <p>
        Dacă ai întrebări sau solicitări legate de această Politică de Confidențialitate sau de
        datele tale, ne poți contacta:
      </p>
      <p>
        📧 <a href="mailto:contact@gab16.com">contact@gab16.com</a><br />
        🌐 <a href="https://gab16.com" target="_blank" rel="noopener">gab16.com</a>
      </p>
      <p>
        Poți solicita ștergerea contului tău oricând trimițând un e-mail la
        <a href="mailto:contact@gab16.com">contact@gab16.com</a>.
      </p>
    </section>

    <section id="changes">
      <h2>11. Modificări ale acestei politici</h2>
      <p>
        Putem actualiza această politică din când în când. Dacă apar modificări semnificative, te
        vom anunța prin e-mail sau printr-o notificare în cadrul Serviciului. Data „Ultima
        actualizare” de la începutul paginii va reflecta cele mai recente modificări.
      </p>
    </section>

    <div class="summary">
      <strong>Pe scurt:</strong>
      Colectăm doar datele necesare (nume de utilizator, e-mail, parolă criptată, imagine de profil
      opțională), nu vindem date și poți solicita oricând ștergerea contului la
      <a href="mailto:contact@gab16.com">contact@gab16.com</a>.
    </div>
  </div>
</template>

<script>
export default {
  name: "PrivacyPageRo",
};
</script>

<style scoped>
.container {
  max-width: 860px;
  margin: 0 auto;
  padding: 24px 16px 48px;
}

h1 {
  font-size: 2rem;
  margin-bottom: 0.25rem;
}

.meta {
  color: #666;
  margin-top: 0;
}

hr {
  margin: 16px 0 24px;
}

h2 {
  font-size: 1.25rem;
  margin-top: 28px;
  margin-bottom: 12px;
}

p,
li {
  line-height: 1.6;
}

.toc {
  background: #fafafa;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 12px 16px;
  margin: 20px 0 28px;
}

.toc ul {
  margin: 0.5rem 0 0.25rem;
  padding-left: 1.25rem;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  margin: 12px 0 8px;
  font-size: 0.95rem;
}

.data-table th,
.data-table td {
  border: 1px solid #e6e6e6;
  padding: 8px 10px;
  vertical-align: top;
}

.data-table thead th {
  background: #f7f7f7;
  text-align: left;
}

.summary {
  margin-top: 28px;
  padding: 12px 14px;
  background: #f8fff4;
  border: 1px solid #d8f5c8;
  border-radius: 8px;
}
</style>
